import { getApiUrl } from "~/utils/api";
import { AESDecrypt } from "./utils";
import { UserStorage } from "./storage";
import { ErrorHandler } from "./errorHandler";
import { requestCache } from "./requestCache";

/**
 * 将对象转换为URL查询字符串
 */
function buildQueryString(params: Record<string, any>): string {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === null || value === undefined) return;

    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item !== null && item !== undefined) {
          searchParams.append(key, String(item));
        }
      });
    } else {
      searchParams.append(key, String(value));
    }
  });

  return searchParams.toString();
}

export function appendQueryString(
  url: string,
  params: Record<string, any>
): string {
  const queryString = buildQueryString(params);
  if (!queryString) return url;

  return url + (url.includes("?") ? "&" : "?") + queryString;
}

// 公共API请求方法，统一处理header和错误码
export interface RequestOptions extends RequestInit {
  headers?: Record<string, string>;
  params?: Record<string, any>;
  method?: string;
  skipAuth?: boolean;
  data?: any;
  cache?: boolean; // 是否启用缓存
  cacheExpiration?: number; // 缓存过期时间（毫秒）
}

async function logout() {
  if (typeof window === "undefined") return;
  UserStorage.clearAuth();
  window.location.href = "/login";
  throw new Error("未登录，已跳转登录");
}

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function waitForSessionId(
  timeout = 1000,
  interval = 200
): Promise<string | null> {
  if (typeof window === "undefined") return null;

  const start = Date.now();
  while (Date.now() - start < timeout) {
    const sid = UserStorage.getSessionId();
    if (sid) return sid;
    await sleep(interval);
  }
  return null;
}
// -------------------------------
// ✔ 主请求方法
// -------------------------------
export async function request<T = any>(
  url: string,
  options: {
    params?: Record<string, any>;
    data?: any;
    method?: string;
    headers?: Record<string, string>;
    cache?: boolean;
    cacheExpiration?: number;
    /** 请求取消信号（通讯助手/轮询等需要中断的请求） */
    signal?: AbortSignal;
    /** 跳过登录态校验（登录页修改密码等未登录接口） */
    skipAuth?: boolean;
  } & { request?: Request } = {}
) {
  const { params, data, method = "GET", cache = false, cacheExpiration, skipAuth = false, ...rest } = options;

  // 检查缓存
  if (cache && typeof window !== "undefined") {
    const cachedData = requestCache.get(url, params, method);
    if (cachedData) {
      return cachedData;
    }
  }

  const headers: Record<string, string> = {
    ...(options.headers || {}),
    "X-Requested-With": "XMLHttpRequest",
  };

  const isFormData = data instanceof FormData;
  if (!isFormData) {
    headers["Content-Type"] = "application/json";
  }

  // -------------------------------
  // ✔ 仅客户端获取 session / tenantId
  // -------------------------------
  let sessionId = "";
  let tenantId = "";
  if (typeof window !== "undefined" && !skipAuth) {
    // 先尝试立即读取
    sessionId = UserStorage.getSessionId() || "";

    // 如果没有，最多等 1000ms 再试（避免刚跳转到业务页时没写完）
    if (!sessionId) {
      const waited = await waitForSessionId(1000);
      sessionId = waited || "";
    }

    const userInfo = UserStorage.getUserInfo();
    if (userInfo) {
      tenantId = `${userInfo?.tenantId}` || "";
    }

    // 还是没有 sessionId，说明确实没登录或登录态丢了，走统一登出
    if (!sessionId || !userInfo || !tenantId) {
      await logout();
    }

    if (sessionId) {
      headers["workspace-session"] = AESDecrypt(sessionId);
    }
  }

  // -------------------------------
  // 构建 URL
  // -------------------------------
  let fullUrl = getApiUrl(url);
  if (method === "GET") {
    fullUrl = appendQueryString(fullUrl, {
      ...params,
      tenantId: `${tenantId}`,
    });
  } else if (params && Object.keys(params).length > 0) {
    fullUrl = appendQueryString(fullUrl, params);
  }

  // -------------------------------
  // 构建 Body
  // -------------------------------
  let body: any = undefined;

  if (isFormData) {
    body = data;
  } else if (method !== "GET" && method !== "HEAD") {
    body =
      headers["Content-Type"] === "application/json"
        ? JSON.stringify({ ...data, tenantId })
        : { ...data, tenantId };
  }

  try {
    const response = await fetch(fullUrl, {
      ...rest,
      method,
      headers,
      credentials: "include",
      ...(body ? { body } : {}),
    });

    if (!response.ok) {
      const text = await response.text();
      const error = new Error(`请求失败: ${response.status}`);
      (error as any).status = response.status;
      (error as any).details = text;

      if (typeof window !== "undefined") {
        ErrorHandler.handleApiError(error);
      }
      throw error;
    }

    const result = await response.json();

    // ========== 修改：调整判断顺序 ==========

    // 1. 先检查新格式（车辆管理接口）：code + success
    if (result.code !== undefined) {
      if (result.code === 200 && result.success) {
        // ✅ 成功响应，直接返回，不进入 ErrorHandler
        if (cache && typeof window !== "undefined") {
          requestCache.set(url, result, params, method, cacheExpiration);
        }
        return result;
      } else {
        // ❌ 失败响应
        const error = new Error(result.message || "请求出错");
        (error as any).code = result.code;
        (error as any).details = result;

        if (typeof window !== "undefined" && !skipAuth) {
          ErrorHandler.handleApiError(error);
        }
        throw error;
      }
    }

    // -------------------------------
    // ✔ 统一错误码处理
    // -------------------------------
    if (result.errcode !== 0) {
      const error = new Error(result.errmsg || "请求出错");
      (error as any).code = result.errcode;
      (error as any).details = result;

      if (typeof window !== "undefined" && !skipAuth) {
        ErrorHandler.handleApiError(error);
      }

      if (result.errcode === 1001001001 && !skipAuth) {
        await logout();
      }

      // 抛出错误给上层
      throw error;
    }
    // 缓存结果
    if (cache && typeof window !== "undefined") {
      requestCache.set(url, result, params, method, cacheExpiration);
    }

    //到这里说明 errcode === 0，正常返回数据
    return result;
  } catch (err) {
    // 主动取消由调用方处理，不当作业务错误弹提示
    if (options.signal?.aborted || (err instanceof Error && err.name === "AbortError")) {
      throw err;
    }
    if (typeof window !== "undefined" && !skipAuth) {
      ErrorHandler.handleApiError(err);
    }
    throw err;
  }
}
